import React from 'react';
import './about.css';
import { FaMapMarkerAlt, FaGraduationCap, FaBriefcase } from 'react-icons/fa';

const About = () => {
  return (
    <section id="about" className="py-20 relative overflow-hidden">
      {/* Grid background matching the App */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background: "#0f172a",
          backgroundImage: `
            linear-gradient(to right, rgba(148,163,184,0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(148,163,184,0.1) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Subtle animated elements that match the grid theme */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-slate-600 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-slate-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-60 h-60 bg-slate-400 rounded-full mix-blend-multiply filter blur-xl opacity-5 animate-pulse delay-500"></div>
      </div>

      {/* Subtle floating particles */}
      <div className="absolute inset-0">
        {React.useMemo(() => 
          [...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-slate-400 rounded-full opacity-20 animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            ></div>
          )), [])}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            À propos de moi
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-slate-400 to-slate-300 mx-auto rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Personal Information */}
          <div className="space-y-6 animate-fade-in delay-200">
            <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Mostafa Al Mannani
              </h3>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Développeur web Full Stack basé à Casablanca, Maroc. Fort d'une formation solide 
                et de plusieurs expériences concrètes, je conçois des applications web modernes, 
                performantes et sur mesure pour les entreprises, les commerçants et les projets innovants.
              </p>
              <div className="flex items-center space-x-3 text-slate-400">
                <span className="text-2xl text-white"><FaMapMarkerAlt /></span>
                <span className="text-lg">Casablanca, Maroc</span>
              </div>
            </div>

            {/* Skills Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-slate-800/40 backdrop-blur-sm p-6 rounded-xl border border-slate-700/50 hover:border-slate-600/50 hover:scale-105 transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-2">Front-end</h4>
                <div className="space-y-2 text-slate-300">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>React.js</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>Next.js</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>HTML/CSS/JS</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>Bootstrap/Tailwind</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/40 backdrop-blur-sm p-6 rounded-xl border border-slate-700/50 hover:border-slate-600/50 hover:scale-105 transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-2">Back-end</h4>
                <div className="space-y-2 text-slate-300">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>Laravel/PHP</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>Node.js</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>Express.js</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>API REST</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/40 backdrop-blur-sm p-6 rounded-xl border border-slate-700/50 hover:border-slate-600/50 hover:scale-105 transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-2">Base de Données</h4>
                <div className="space-y-2 text-slate-300">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>MySQL</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <span>MongoDB</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Experience & Education */}
          <div className="space-y-6 animate-fade-in delay-400">
            {/* Education */}
            <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <span className="text-3xl mr-3"><FaGraduationCap /></span>
                Formations
              </h3>
              <div className="space-y-6">
                <div className="border-l-4 border-slate-400 pl-4">
                  <h4 className="text-lg font-semibold text-white">Certification MERN Full Stack</h4>
                  <p className="text-slate-300">Nouacer Wings Tech - JADARA(ONG)</p>
                  <p className="text-slate-400 text-sm">2025</p>
                </div>
                <div className="border-l-4 border-slate-400 pl-4">
                  <h4 className="text-lg font-semibold text-white">Diplôme de Technicien Spécialisé</h4>
                  <p className="text-slate-300">Développement Digital – OFPPT</p>
                  <p className="text-slate-400 text-sm">2022–2024</p>
                </div>
                {/* <div className="border-l-4 border-slate-400 pl-4">
                  <h4 className="text-lg font-semibold text-white">Formation Soft Skills</h4>
                  <p className="text-slate-300">Employabilité – Amideast</p>
                </div> */}
              </div>
            </div>

            {/* Experience */}
            <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <span className="text-3xl mr-3"><FaBriefcase /></span>
                Expériences professionnelles
              </h3>
              <div className="space-y-6">
                 <div className="border-l-4 border-slate-400 pl-4">
                  <h4 className="text-lg font-semibold text-white">Développeur Web Full Stack</h4>
                  <p className="text-slate-300">Aditya, Casablanca</p>
                  <p className="text-slate-400 text-sm"> Décembre 2025 – Présent</p>
                </div>
                <div className="border-l-4 border-slate-400 pl-4">
                  <h4 className="text-lg font-semibold text-white">Développeur Web Full Stack</h4>
                  <p className="text-slate-300">SoftSevenArt, Casablanca</p>
                  <p className="text-slate-400 text-sm">Octobre 2024 – Février 2025</p>
                </div>
                <div className="border-l-4 border-slate-400 pl-4">
                  <h4 className="text-lg font-semibold text-white">Développeur Web Full Stack</h4>
                  <p className="text-slate-300">Oracea Services, Casablanca</p>
                  <p className="text-slate-400 text-sm">Mars 2024 – Avril 2024</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="mt-16 text-center animate-fade-in delay-600">
          <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
            <h3 className="text-2xl font-bold text-white mb-4">Mon objectif</h3>
            <p className="text-lg text-slate-300 leading-relaxed max-w-4xl mx-auto">
              Contribuer à la réussite des projets web en apportant des solutions efficaces, 
              modernes et adaptées aux besoins réels des clients. Je propose des solutions 
              digitales personnalisées pour les entreprises, notamment dans les secteurs du 
              commerce et de l'agriculture intelligente.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;